// Tiny module-scope response cache so page revisits paint instantly with the
// last seen payload while a background refetch updates the data. Used by
// Inventory, Sales, Listings, and InventoryDetail. Entries are kept in an LRU
// (most-recent-write at the back) capped to MAX_ENTRIES.
//
// This is intentionally NOT React state — the cache must survive component
// unmount/remount, which is exactly what makes a return visit feel instant.

const MAX_ENTRIES = 80;
const _cache = new Map();

function bump(key) {
  if (_cache.has(key)) {
    const v = _cache.get(key);
    _cache.delete(key);
    _cache.set(key, v);
  }
}

export function cacheGet(key) {
  if (!key) return null;
  bump(key);
  return _cache.get(key) || null;
}

export function cacheSet(key, data) {
  if (!key) return;
  if (_cache.has(key)) _cache.delete(key);
  _cache.set(key, { data, ts: Date.now() });
  while (_cache.size > MAX_ENTRIES) {
    const oldest = _cache.keys().next().value;
    if (oldest === undefined) break;
    _cache.delete(oldest);
  }
}

export function cacheInvalidate(prefix) {
  if (!prefix) return;
  for (const k of [..._cache.keys()]) {
    if (k.startsWith(prefix)) _cache.delete(k);
  }
}

// Stable cache key from path + a params object. Keys are sorted so callers
// don't have to think about object insertion order.
export function cacheKey(path, params) {
  if (!params || Object.keys(params).length === 0) return path;
  const sorted = Object.keys(params).sort();
  const pairs = sorted.map((k) => `${k}=${JSON.stringify(params[k] ?? "")}`);
  return `${path}?${pairs.join("&")}`;
}
